import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { CompareBucketProvider } from "./context/CompareBucketContext";
import { OwnedGridsProvider } from "./context/OwnedGridsContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Analysis from "./pages/Analysis";
import Comparison from "./pages/Comparison";
import Assistant from "./pages/Assistant";
import DeepAnalysis from "./pages/DeepAnalysis";
import DeepAnalysisPrint from "./pages/DeepAnalysisPrint";
import SharedReport from "./pages/SharedReport";
import BranchRadar from "./pages/BranchRadar";
import Skenario from "./pages/Skenario";
import Onboarding from "./pages/Onboarding";
import LandingPage from "./pages/LandingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FleiPage from "./events/flei/FleiPage";
import { onPlatformHost } from "./lib/hosts";
import AuthPage from "./pages/AuthPage";
import ManageProfile from "./pages/ManageProfile";
import Account from "./pages/Account";
import { setToken, getActiveProfile, me } from "./lib/api";
import { CustomProjectShell, CUSTOM_PROJECT_ROUTE } from "./custom";

function GoogleDone() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();

  useEffect(() => {
    const token = params.get("token");
    const rt    = params.get("rt");
    if (!token) { window.location.replace("/auth"); return; }
    setToken(token);
    // Set httpOnly refresh cookie via backend, then fetch user + profile
    const finalize = rt
      ? fetch("/api/auth/google/finalize", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rt }),
        })
      : Promise.resolve();
    finalize
      .then(() => Promise.all([me(), getActiveProfile()]))
      .then(([user, profile]) => {
        setUser(user);
        window.location.replace(profile ? "/analysis" : "/onboarding");
      })
      .catch(() => { setUser(null); window.location.replace("/auth"); });
  }, []);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
    </div>
  );
}

// web-pro is the Pro app: only pro_* tiers may enter. A Lite account is sent to
// a block screen (the Lite app lives at a different URL).
const OTHER_APP_URL = import.meta.env.VITE_LITE_URL || "";
function tierAllowed(user) {
  if (!user?.tier) return true;                 // unknown tier → don't lock out
  return String(user.tier).startsWith("pro");
}

function TierBlock() {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center bg-slate-50">
      <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-2xl">🔒</div>
      <h1 className="text-xl font-bold text-slate-800">Ini adalah akun Lite</h1>
      <p className="text-sm text-slate-500 max-w-sm">
        <span className="font-medium text-slate-700">{user?.email}</span> menggunakan paket Lite, yang menggunakan aplikasi Landhep Lite. Aplikasi Pro ini tidak tersedia untuk akun Anda.
      </p>
      <div className="flex items-center gap-3 mt-1">
        {OTHER_APP_URL && (
          <a href={OTHER_APP_URL} className="rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-bold text-white hover:bg-teal-800 transition-colors">Buka Landhep Lite</a>
        )}
        <button onClick={logout} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors">Keluar</button>
      </div>
    </div>
  );
}

function AuthGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!tierAllowed(user)) return <TierBlock />;
  return children;
}

function hasProfile() {
  try {
    const p = JSON.parse(localStorage.getItem("landhep_profile") || "{}");
    return !!p.city?.kode_kabkot;
  } catch { return false; }
}

// Public routes: redirect signed-in users into the app.
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return children;
  return <Navigate to={hasProfile() ? "/analysis" : "/onboarding"} replace />;
}

// Requires auth, but no profile needed (onboarding itself).
function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  // `?new=1` = the user deliberately came here from the profile switcher to add a
  // SECOND business (its own profile — the business category is locked per profile).
  const addingNew = new URLSearchParams(window.location.search).get("new") === "1";
  if (loading) return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  if (hasProfile() && !addingNew) return <Navigate to="/analysis" replace />;
  return children;
}

// Requires auth AND a completed onboarding profile.
function ProfileGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  if (!tierAllowed(user)) return <TierBlock />;
  if (!hasProfile()) return <Navigate to="/onboarding" replace />;
  return children;
}

function MapLayout({ children }) {
  return (
    <div className="relative h-full min-h-[100dvh]">
      <Navbar />
      <Sidebar />
      {children}
    </div>
  );
}

function PageLayout({ children }) {
  return (
    <div className="relative h-full bg-gray-50">
      <Navbar />
      <Sidebar />
      <div className="h-full pt-[84px] pl-24 overflow-auto">
        {children}
      </div>
    </div>
  );
}

const ON_PLATFORM = onPlatformHost();

function App() {
  return (
    <AuthProvider>
    <OwnedGridsProvider>
    <CompareBucketProvider>
      <BrowserRouter>
        <Routes>
          {/* Google OAuth callback handler */}
          <Route path="/auth/google/done" element={<GoogleDone />} />

          {/* Public routes */}
          {/* On platform.landhep.id the marketing page has no business being rendered —
              a client-side hop to "/" (logout, a "back" link) lands on sign-in instead. */}
          <Route path="/" element={ON_PLATFORM ? <Navigate to="/auth" replace /> : <PublicOnly><LandingPage /></PublicOnly>} />
          <Route path="/privasi" element={<PrivacyPolicy />} />
          {/* Event page — public, readable signed in or not. See src/events/flei/README.md. */}
          <Route path="/flei" element={<FleiPage />} />
          <Route path="/landing" element={ON_PLATFORM ? <Navigate to="/auth" replace /> : <PublicOnly><LandingPage /></PublicOnly>} />
          <Route path="/home" element={ON_PLATFORM ? <Navigate to="/auth" replace /> : <PublicOnly><LandingPage /></PublicOnly>} />
          <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />

          {/* Onboarding — auth required, no profile needed yet */}
          <Route path="/onboarding" element={<OnboardingGate><Onboarding /></OnboardingGate>} />

          {/* Protected routes — require auth + completed onboarding */}
          <Route path="/analysis" element={<ProfileGate><MapLayout><Analysis /></MapLayout></ProfileGate>} />
          <Route path="/compare" element={<ProfileGate><PageLayout><Comparison /></PageLayout></ProfileGate>} />
          <Route path="/deep-analysis" element={<ProfileGate><PageLayout><DeepAnalysis /></PageLayout></ProfileGate>} />
          {/* The PDF: the report printed by the browser, no app chrome (opened in a new tab by
              "Unduh Laporan PDF"). Public: its data comes from the opener's ticket, never from
              the API, and a shared report's recipient has no login. */}
          <Route path="/print/deep-analysis" element={<DeepAnalysisPrint />} />
          {/* A shared report — public, by token, optionally behind a password */}
          <Route path="/laporan/:token" element={<SharedReport />} />
          <Route path="/assistant" element={<ProfileGate><MapLayout><Assistant /></MapLayout></ProfileGate>} />
          <Route path="/branch-radar" element={<ProfileGate><MapLayout><BranchRadar /></MapLayout></ProfileGate>} />
          <Route path="/skenario" element={<ProfileGate><MapLayout><Skenario /></MapLayout></ProfileGate>} />
          {/* Legacy path → keep old bookmarks working */}
          <Route path="/demand-scenarios" element={<Navigate to="/branch-radar" replace />} />
          <Route path="/profile" element={<AuthGate><PageLayout><ManageProfile /></PageLayout></AuthGate>} />
          <Route path="/account" element={<AuthGate><PageLayout><Account /></PageLayout></AuthGate>} />
          <Route
            path={`${CUSTOM_PROJECT_ROUTE}/*`}
            element={<ProfileGate><PageLayout><CustomProjectShell /></PageLayout></ProfileGate>}
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/analysis" replace />} />
        </Routes>
      </BrowserRouter>
    </CompareBucketProvider>
    </OwnedGridsProvider>
    </AuthProvider>
  );
}

export default App;
